import React, { useState, useEffect, useCallback } from 'react'
import {
  Shield,
  Users,
  Mail,
  UserCheck,
  CheckCircle,
  RotateCw,
  XCircle,
  Plus,
  Copy,
  Check,
  Info,
} from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { teamService } from '../team/teamService'
import { AdminUserItem, AdminInvitationItem } from '../../types/team'
import { ManagePermissionsModal } from './ManagePermissionsModal'

export const UsersAndPermissionsView: React.FC = () => {
  const { user } = useAuth()
  const defaultOrgId = '11111111-1111-1111-1111-111111111111'
  const defaultTeamId = user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

  const [activeTab, setActiveTab] = useState<'users' | 'invitations' | 'matrix'>('users')
  const [usersList, setUsersList] = useState<AdminUserItem[]>([])
  const [invitationsList, setInvitationsList] = useState<AdminInvitationItem[]>([])
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [toastMsg, setToastMsg] = useState<string | null>(null)

  // Modales
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<AdminUserItem | null>(null)
  const [isInviteStaffModalOpen, setIsInviteStaffModalOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('coach')
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false)
  const [inviteResult, setInviteResult] = useState<{ token: string; email: string } | null>(null)
  const [copiedLink, setCopiedLink] = useState(false)

  const isAuthorizedAdmin = user?.is_system_admin || user?.memberships.some((m) =>
    m.roles.includes('admin') || m.capabilities?.includes('manage_users')
  )

  const loadData = useCallback(async () => {
    if (!isAuthorizedAdmin) return
    setLoading(true)
    setErrorMsg(null)
    try {
      const data = await teamService.getAdminUsersAndPermissions(defaultOrgId)
      setUsersList(data.users)
      setInvitationsList(data.invitations)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar usuarios y permisos.')
    } finally {
      setLoading(false)
    }
  }, [defaultOrgId, isAuthorizedAdmin])

  useEffect(() => {
    loadData()
  }, [loadData])

  const showToast = (msg: string) => {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(null), 4000)
  }

  const handleResend = async (invId: string) => {
    try {
      const res = await teamService.resendInvitation(invId)
      if (res.success) {
        showToast('Invitación reenviada con nuevo token de 7 días.')
        loadData()
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al reenviar invitación.')
    }
  }

  const handleRevoke = async (invId: string) => {
    if (!window.confirm('¿Deseas revocar esta invitación? El token quedará invalidado inmediatamente.')) return
    try {
      const res = await teamService.revokeInvitation(invId)
      if (res.success) {
        showToast('Invitación revocada correctamente.')
        loadData()
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al revocar invitación.')
    }
  }

  const handleInviteStaff = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      alert('Introduce un correo electrónico válido.')
      return
    }

    setIsSubmittingInvite(true)
    try {
      const res = await teamService.inviteUser({
        organizationId: defaultOrgId,
        teamId: defaultTeamId,
        email: inviteEmail.trim().toLowerCase(),
        roles: [inviteRole],
      })
      if (res.success) {
        setInviteResult({ token: res.token, email: res.email })
        showToast(`Invitación de ${inviteRole} generada para ${res.email}.`)
        loadData()
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Error al emitir invitación.')
    } finally {
      setIsSubmittingInvite(false)
    }
  }

  if (!isAuthorizedAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Shield size={36} style={{ color: 'var(--brand-red)', marginBottom: '0.75rem', opacity: 0.8 }} />
        <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.35rem' }}>
          Acceso Restringido a Administradores
        </h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', maxWidth: '420px', margin: '0 auto' }}>
          La gestión técnica de usuarios, asignaciones y permisos requiere autorización explícita de administración en la organización.
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Cabecera */}
      <div
        style={{
          marginBottom: '1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.2rem', color: 'var(--text-main)' }}>
            Administración y Permisos
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            Control de identidades, roles deportivos, capacidades efectivas e invitaciones de la organización.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem' }}
            onClick={() => {
              setInviteResult(null)
              setInviteEmail('')
              setInviteRole('coach')
              setIsInviteStaffModalOpen(true)
            }}
          >
            <Plus size={15} />
            <span>Invitar Usuario / Staff</span>
          </button>
        </div>
      </div>

      {toastMsg && (
        <div
          role="status"
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--status-success-bg)',
            color: 'var(--status-success-text)',
            border: '1px solid var(--status-success-border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.82rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <CheckCircle size={16} />
          <div>{toastMsg}</div>
        </div>
      )}

      {/* Pestañas */}
      <div className="sports-nav-tabs" style={{ marginBottom: '1.25rem' }}>
        <button
          type="button"
          className={`sports-nav-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={16} />
          <span>Usuarios y Cuentas ({usersList.length})</span>
        </button>

        <button
          type="button"
          className={`sports-nav-btn ${activeTab === 'invitations' ? 'active' : ''}`}
          onClick={() => setActiveTab('invitations')}
        >
          <Mail size={16} />
          <span>Invitaciones ({invitationsList.length})</span>
        </button>

        <button
          type="button"
          className={`sports-nav-btn ${activeTab === 'matrix' ? 'active' : ''}`}
          onClick={() => setActiveTab('matrix')}
        >
          <Shield size={16} />
          <span>Matriz Normativa</span>
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="table-container">
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Cargando usuarios y membresías...
            </div>
          ) : errorMsg ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--status-danger-text)' }}>
              {errorMsg}
            </div>
          ) : usersList.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No hay usuarios registrados en la organización.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Equipo Asignado</th>
                  <th>Roles</th>
                  <th>Capacidades Específicas</th>
                  <th>Estado</th>
                  <th style={{ width: '100px', textAlign: 'right' }}>Gestión</th>
                </tr>
              </thead>
              <tbody>
                {usersList.map((u) => (
                  <tr key={u.membership_id || u.user_id}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                        {u.full_name}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {u.email} {u.is_system_admin && '· (Admin Sistema)'}
                      </div>
                    </td>

                    <td>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {u.team_name || 'Organización (Global)'}
                      </span>
                    </td>

                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {u.roles.map((r) => (
                          <span
                            key={r}
                            className={`badge ${
                              r === 'admin'
                                ? 'badge-red'
                                : r === 'coach'
                                ? 'badge-info'
                                : r === 'medic'
                                ? 'badge-success'
                                : 'badge-subtle'
                            }`}
                            style={{ fontSize: '0.72rem' }}
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td>
                      {u.capabilities && u.capabilities.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {u.capabilities.map((c) => (
                            <span key={c} className="badge badge-muted" style={{ fontSize: '0.68rem' }}>
                              {c}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          Heredadas por rol
                        </span>
                      )}
                    </td>

                    <td>
                      {u.is_active ? (
                        <span className="badge badge-success">Activo</span>
                      ) : (
                        <span className="badge badge-red">Revocado</span>
                      )}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                        onClick={() => setSelectedUserForEdit(u)}
                      >
                        Permisos
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'invitations' && (
        <div className="table-container">
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Cargando invitaciones...
            </div>
          ) : invitationsList.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No hay invitaciones registradas en la organización.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Destinatario / Atleta</th>
                  <th>Roles</th>
                  <th>Estado</th>
                  <th>Caducidad</th>
                  <th>Emitido por</th>
                  <th style={{ width: '130px', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {invitationsList.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                        {inv.email}
                      </div>
                      {inv.token_masked && (
                        <div style={{ fontSize: '0.70rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                          Token: {inv.token_masked}
                        </div>
                      )}
                      {inv.athlete_name && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--brand-blue)' }}>
                          Ficha vinculada: {inv.athlete_name}
                        </div>
                      )}
                    </td>

                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {inv.roles.map((r) => (
                          <span key={r} className="badge badge-subtle" style={{ fontSize: '0.72rem' }}>
                            {r}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td>
                      {inv.status === 'pending' && (
                        <span className="badge badge-yellow">Pendiente</span>
                      )}
                      {inv.status === 'accepted' && (
                        <span className="badge badge-success">Aceptada</span>
                      )}
                      {inv.status === 'expired' && (
                        <span className="badge badge-red">Expirada</span>
                      )}
                      {inv.status === 'revoked' && (
                        <span className="badge badge-muted">Revocada</span>
                      )}
                    </td>

                    <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {new Date(inv.expires_at).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </td>

                    <td style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {inv.invited_by_name || 'Sistema'}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.35rem' }}>
                        {inv.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{ padding: '0.25rem', color: 'var(--brand-blue)' }}
                              title="Reenviar invitación (invalida el token previo)"
                              onClick={() => handleResend(inv.id)}
                            >
                              <RotateCw size={14} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{ padding: '0.25rem', color: 'var(--brand-red)' }}
                              title="Revocar invitación"
                              onClick={() => handleRevoke(inv.id)}
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                        {inv.status !== 'pending' && (
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Cerrada</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'matrix' && (
        <div className="table-container">
          <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--surface-border)' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>
              Matriz Normativa de Roles y Capacidades (docs/PERMISSIONS_MATRIX.md)
            </h3>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Aislamiento estricto de notas clínicas: los administradores técnicos y entrenadores NO tienen acceso médico.
            </p>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Capacidad Efectiva</th>
                <th>Admin</th>
                <th>Coach</th>
                <th>Asistente</th>
                <th>Sanitario</th>
                <th>Atleta</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Gestión deportiva del equipo (`manage_team_sports`)</td>
                <td>❌</td><td>✅</td><td>❌</td><td>❌</td><td>❌</td>
              </tr>
              <tr>
                <td>Gestión de jugadores y seguimiento (`manage_roster`)</td>
                <td>❌</td><td>✅</td><td>❌</td><td>❌</td><td>❌</td>
              </tr>
              <tr>
                <td>Invitar atletas vinculados (`invite_athletes`)</td>
                <td>❌</td><td>✅</td><td>❌</td><td>❌</td><td>❌</td>
              </tr>
              <tr>
                <td>Convocar participantes (`manage_events`)</td>
                <td>❌</td><td>✅</td><td>❌</td><td>❌</td><td>❌</td>
              </tr>
              <tr>
                <td>Preparar catálogos y guiones (`manage_playbook`)</td>
                <td>❌</td><td>✅</td><td>❌</td><td>❌</td><td>❌</td>
              </tr>
              <tr>
                <td>Captura en vivo de campo (`capture_field`)</td>
                <td>❌</td><td>❌</td><td>✅</td><td>❌</td><td>❌</td>
              </tr>
              <tr>
                <td>Estadísticas colectivas (`view_stats`)</td>
                <td>❌</td><td>✅</td><td>✅</td><td>❌</td><td>❌ (Solo propias)</td>
              </tr>
              <tr>
                <td>Carga deportiva sRPE (`view_workload`)</td>
                <td>❌</td><td>✅</td><td>❌</td><td>✅</td><td>❌ (Solo propias)</td>
              </tr>
              <tr>
                <td>Cumplimiento de formularios (`view_form_compliance`)</td>
                <td>❌</td><td>✅</td><td>❌</td><td>✅</td><td>❌ (Solo propios)</td>
              </tr>
              <tr style={{ backgroundColor: 'rgba(239, 68, 68, 0.03)' }}>
                <td style={{ fontWeight: 600, color: 'var(--brand-red)' }}>
                  Salud privada y notas clínicas (`manage_health`)
                </td>
                <td>❌</td><td>❌</td><td>❌</td><td>✅</td><td>❌ (Solo propios)</td>
              </tr>
              <tr>
                <td>Publicar disponibilidad (`publish_availability`)</td>
                <td>❌</td><td>❌</td><td>❌</td><td>✅</td><td>❌</td>
              </tr>
              <tr>
                <td>Administrar usuarios y permisos (`manage_users`)</td>
                <td>✅</td><td>❌</td><td>❌</td><td>❌</td><td>❌</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Modal para editar permisos */}
      <ManagePermissionsModal
        isOpen={!!selectedUserForEdit}
        onClose={() => setSelectedUserForEdit(null)}
        onSuccess={(msg) => {
          showToast(msg)
          loadData()
        }}
        userItem={selectedUserForEdit}
      />

      {/* Modal para invitar staff */}
      {isInviteStaffModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsInviteStaffModalOpen(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: '460px', width: '92vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <UserCheck size={18} style={{ color: 'var(--brand-red)' }} />
                <h2 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                  Invitar Usuario / Staff
                </h2>
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setIsInviteStaffModalOpen(false)}
              >
                <XCircle size={18} />
              </button>
            </div>

            <div className="modal-body">
              {!inviteResult ? (
                <form onSubmit={handleInviteStaff}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="usuario@equipo.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                      Rol a Asignar *
                    </label>
                    <select
                      className="form-control"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                    >
                      <option value="coach">Entrenador (Coach)</option>
                      <option value="assistant_coach">Entrenador Asistente / Preparador</option>
                      <option value="medic">Sanitario / Fisioterapeuta</option>
                      <option value="admin">Administrador de Organización</option>
                      <option value="athlete">Atleta / Jugador</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setIsInviteStaffModalOpen(false)}
                      disabled={isSubmittingInvite}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmittingInvite}
                    >
                      {isSubmittingInvite ? 'Generando...' : 'Generar Invitación'}
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  <div
                    style={{
                      padding: '0.75rem',
                      backgroundColor: 'var(--status-success-bg)',
                      color: 'var(--status-success-text)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.82rem',
                      marginBottom: '1rem',
                    }}
                  >
                    Invitación generada correctamente para {inviteResult.email}.
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                        Enlace de Activación para Entrega Manual:
                      </label>
                      <span className="badge badge-yellow" style={{ fontSize: '0.68rem' }}>
                        Entrega manual · SMTP no configurado
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        readOnly
                        value={`${window.location.origin}/?token=${inviteResult.token}`}
                        className="form-control"
                        style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap' }}
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/?token=${inviteResult.token}`)
                          setCopiedLink(true)
                          setTimeout(() => setCopiedLink(false), 3000)
                        }}
                      >
                        {copiedLink ? <Check size={14} /> : <Copy size={14} />}
                        <span>{copiedLink ? 'Copiado' : 'Copiar enlace'}</span>
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      padding: '0.65rem 0.75rem',
                      backgroundColor: 'var(--surface-subtle)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--surface-border)',
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.45rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <Info size={14} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--brand-blue)' }} />
                    <div>
                      <strong>Canal de entrega:</strong> Entrega manual requerida. El envío automático por correo permanece pendiente por falta de configuración de Custom SMTP en Supabase Cloud.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => {
                      setIsInviteStaffModalOpen(false)
                      setInviteResult(null)
                    }}
                  >
                    Entendido / Cerrar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
